import axios, { AxiosRequestConfig } from 'axios';
import { objectToQueryString } from '@/utils/common';
import { APIPrefix } from '@/enums/api.enum';

export class ApiFetcher {
  static defaultHeaders = {
    'Content-Type': 'application/json',
    'x-api-key': import.meta.env.VITE_X_API_KEY
  };

  public readonly apiUrl: string;

  constructor(host: string, type: APIType = APIPrefix.Admin) {
    this.apiUrl = `${host}/api/${type}`; // Dynamically set to APIPrefix.Admin or 'client'
  }

  static getAccessToken() {
    return localStorage.getItem('access_token') || undefined;
  }

  static getRefreshToken() {
    return localStorage.getItem('refresh_token') || undefined;
  }

  static isTokenExpired(token?: string) {
    if (!token) return true;
    try {
      const base64Url = token.split('.')[1];
      if (!base64Url) return true;
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        window
          .atob(base64)
          .split('')
          .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );
      const payload = JSON.parse(jsonPayload);
      if (!payload || typeof payload.exp !== 'number') return false;
      return Date.now() / 1000 > payload.exp;
    } catch {
      return true;
    }
  }

  static async request(url: string, options: AxiosRequestConfig): Promise<any> {
    const token = ApiFetcher.getAccessToken();
    const refreshToken = ApiFetcher.getRefreshToken();

    const localTestVal = import.meta.env.VITE_LOCAL_TEST;
    const isLocalTest = localTestVal === 'true' || localTestVal === '"true"';

    if (!isLocalTest && ApiFetcher.isTokenExpired(token) && refreshToken) {

      try {
        const headers = {
          ...ApiFetcher.defaultHeaders,
          Authorization: `Bearer ${refreshToken}`
        };
        const response = await axios({
          url,
          ...options,
          headers
        });

        return response.data;
      } catch (error: any) {
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        if (window.location.pathname !== '/login') {
          window.location.href = '/login';
        }
        throw error.response || error;
      }
    }

    const headersWithToken = {
      ...ApiFetcher.defaultHeaders,
      ...(token && { Authorization: `Bearer ${token}` })
    };

    const response = await axios({
      url,
      ...options,
      headers: { ...headersWithToken, ...options.headers }
    });
    return response.data;
  }

  async get(
    url: string,
    {
      query = {},
      ...options
    }: AxiosRequestConfig & { query?: Record<string, any> } = {}
  ) {
    const fullUrl = url.startsWith('http')
      ? url
      : `${this.apiUrl}/${url}${objectToQueryString(query)}`;

    return ApiFetcher.request(fullUrl, {
      method: 'GET',
      ...options
    });
  }

  async post(
    url: string,
    data: Record<string, any> = {},
    options: AxiosRequestConfig = {}
  ) {
    const fullUrl = url.startsWith('http') ? url : `${this.apiUrl}/${url}`;
    return ApiFetcher.request(fullUrl, {
      method: 'POST',
      data,
      ...options
    });
  }

  async put(
    url: string,
    data: Record<string, any> = {},
    options: AxiosRequestConfig = {}
  ) {
    const fullUrl = url.startsWith('http') ? url : `${this.apiUrl}/${url}`;
    return ApiFetcher.request(fullUrl, {
      method: 'PUT',
      data,
      ...options
    });
  }

  async patch(
    url: string,
    data: Record<string, any> = {},
    options: AxiosRequestConfig = {}
  ) {
    const fullUrl = url.startsWith('http') ? url : `${this.apiUrl}/${url}`;
    return ApiFetcher.request(fullUrl, {
      method: 'PATCH',
      data,
      ...options
    });
  }

  async delete(url: string, options: AxiosRequestConfig = {}) {
    const fullUrl = url.startsWith('http') ? url : `${this.apiUrl}/${url}`;
    return ApiFetcher.request(fullUrl, {
      method: 'DELETE',
      ...options
    });
  }
}
